import React, { useState, useEffect } from 'react';
import { Calculator, Clock, DollarSign, Receipt, ChevronLeft, ChevronRight, ArrowLeft, AlertCircle, FileText } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { useApplicationSettingsContext } from '../contexts/ApplicationSettingsContext';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { supabase } from '../lib/supabaseClient';
import BetaPaymentsModal from '../components/BetaPaymentsModal';
import ApplyProductSalePaymentModal from '../components/ApplyProductSalePaymentModal';
import RecordPaymentModal from '../components/RecordPaymentModal';
import { InvoiceSettingsService } from '../services/invoiceSettingsService';

// Import existing page content (we'll need to refactor these into components)
import TimeclockPage from './Timeclock';
import ManagePayrollPage from './ManagePayroll';

// Time/Accounting sub-pages configuration
const timeAccountingPages = [
  // {
  //   id: 'accounting-hub',
  //   label: 'Accounting Hub',
  //   icon: Calculator,
  //   description: 'Main accounting dashboard and tools'
  // },
  {
    id: 'invoice-payments',
    label: 'Invoice Payments',
    icon: Receipt,
    description: 'Manage and track invoice payments'
  },
  {
    id: 'timeclock',
    label: 'Timeclock',
    icon: Clock,
    description: 'Employee time tracking and management'
  },
  {
    id: 'payroll',
    label: 'Payroll',
    icon: DollarSign,
    description: 'Payroll processing and management'
  },
  {
    id: 'invoice-settings',
    label: 'Invoice Settings',
    icon: Receipt,
    description: 'Configure invoice templates and settings'
  }
];

// Component for accounting hub
function AccountingHubContent() {
  return (
    <div className="h-full overflow-y-auto bg-gray-100">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Accounting Hub</h1>
          <p className="text-gray-600">Financial overview and management tools</p>
        </div>

        {/* Coming Soon Message */}
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className="mb-6">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calculator className="h-10 w-10 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Coming Soon</h2>
              <p className="text-gray-600 mb-6">
                We're working hard to bring you a comprehensive accounting dashboard with financial insights, 
                revenue tracking, and business analytics.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Coming</h3>
              <div className="space-y-3 text-left">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Financial overview dashboard</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Revenue tracking and analytics</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Invoice and payment management</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-700">Business performance insights</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Component for timeclock content
function TimeclockContent() {
  return <TimeclockPage />;
}

// Component for invoice payments content
function InvoicePaymentsContent() {
  const { selectedBusiness } = useBusiness();
  const [bookings, setBookings] = useState<any[]>([]);
  const [productSales, setProductSales] = useState<any[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [isPaymentsModalOpen, setIsPaymentsModalOpen] = useState(false);
  const [isApplyPaymentModalOpen, setIsApplyPaymentModalOpen] = useState(false);
  const [selectedProductSale, setSelectedProductSale] = useState<any>(null);
  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] = useState(false);
  const [selectedBookingForPayment, setSelectedBookingForPayment] = useState<any>(null);
  const [dateFilter, setDateFilter] = useState<'this-month' | 'last-month' | 'this-year' | 'all-time'>('this-month');

  // Date filtering function with timezone handling
  const getDateRange = (filter: string) => {
    const now = new Date();
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    console.log('🔍 Date Range Debug:', {
      currentDate: now.toISOString(),
      userTimezone,
      filter
    });
    
    // Create dates in user's timezone
    const startOfDay = (date: Date) => {
      return new Date(date.toLocaleString('en-US', { timeZone: userTimezone, year: 'numeric', month: '2-digit', day: '2-digit' }) + ' 00:00:00');
    };
    
    const endOfDay = (date: Date) => {
      return new Date(date.toLocaleString('en-US', { timeZone: userTimezone, year: 'numeric', month: '2-digit', day: '2-digit' }) + ' 23:59:59');
    };

    let result;
    switch (filter) {
      case 'this-month':
        const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        result = {
          start: startOfDay(startOfThisMonth),
          end: endOfDay(now)
        };
        break;
      case 'last-month':
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        result = {
          start: startOfDay(startOfLastMonth),
          end: endOfDay(endOfLastMonth)
        };
        break;
      case 'this-year':
        const startOfThisYear = new Date(now.getFullYear(), 0, 1);
        result = {
          start: startOfDay(startOfThisYear),
          end: endOfDay(now)
        };
        break;
      case 'all-time':
      default:
        result = {
          start: new Date(0), // Beginning of time
          end: endOfDay(now)
        };
    }
    
    console.log('🔍 Date Range Result:', {
      filter,
      start: result.start.toISOString(),
      end: result.end.toISOString()
    });
    
    return result;
  };

  // Combine and filter invoices (bookings + product sales) based on date range
  useEffect(() => {
    // Combine bookings and product sales into unified invoice list
    const allInvoices = [
      ...bookings.map(booking => ({
        ...booking,
        type: 'booking',
        invoice_number: booking.booking_number,
        invoice_date: booking.end_date || booking.created_at,
        invoice_total: booking.total_amount
      })),
      ...productSales.map(sale => ({
        ...sale,
        type: 'product_sale',
        invoice_number: sale.sale_number,
        invoice_date: sale.created_at,
        invoice_total: sale.total_amount
      }))
    ].sort((a, b) => {
      // Sort by date descending (newest first)
      const dateA = new Date(a.invoice_date);
      const dateB = new Date(b.invoice_date);
      return dateB.getTime() - dateA.getTime();
    });

    if (dateFilter === 'all-time') {
      setFilteredInvoices(allInvoices);
    } else {
      const { start, end } = getDateRange(dateFilter);
      console.log('🔍 Date Filter Debug:', {
        filter: dateFilter,
        start: start.toISOString(),
        end: end.toISOString(),
        totalBookings: bookings.length,
        totalProductSales: productSales.length,
        totalInvoices: allInvoices.length
      });
      
      const filtered = allInvoices.filter(invoice => {
        const invoiceDate = new Date(invoice.invoice_date);
        const isInRange = invoiceDate >= start && invoiceDate <= end;
        return isInRange;
      }).sort((a, b) => {
        // Sort filtered results by date descending (newest first)
        const dateA = new Date(a.invoice_date);
        const dateB = new Date(b.invoice_date);
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log('🔍 Filtered Results:', {
        originalCount: allInvoices.length,
        filteredCount: filtered.length
      });
      
      setFilteredInvoices(filtered);
    }
  }, [bookings, productSales, dateFilter]);

  // Fetch all bookings and product sales with payment data
  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!selectedBusiness?.id) return;
      
      setIsLoading(true);
      try {
        // Fetch bookings
        const { data: bookingsData, error: bookingsError } = await supabase
          .from('bookings')
          .select(`
            id,
            booking_number,
            total_amount,
            status,
            created_at,
            end_date,
            customer:customers(
              id,
              first_name,
              last_name,
              email,
              phone
            ),
            payments:payments_with_stripe_details(*)
          `)
          .eq('business_id', selectedBusiness.id)
          .eq('status', 'completed')
          .order('end_date', { ascending: false });

        if (bookingsError) throw bookingsError;
        setBookings(bookingsData || []);

        // Fetch product sales
        const { data: productSalesData, error: productSalesError } = await supabase
          .from('product_sales')
          .select(`
            id,
            sale_number,
            total_amount,
            subtotal_amount,
            tax_amount,
            tax_rate,
            discount_amount,
            payment_status,
            business_id,
            customer_id,
            created_at,
            customer:customers(
              id,
              first_name,
              last_name,
              email,
              phone
            ),
            payments(*),
            items:product_sale_items(
              id,
              product_name,
              quantity,
              unit_price,
              total_price
            )
          `)
          .eq('business_id', selectedBusiness.id)
          .order('created_at', { ascending: false });

        if (productSalesError) throw productSalesError;
        setProductSales(productSalesData || []);

      } catch (error) {
        console.error('Error fetching invoice data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoiceData();
  }, [selectedBusiness]);

  // Calculate payment status for an invoice (booking or product sale)
  const getPaymentStatus = (invoice: any) => {
    // For product sales, use payment_status if available
    if (invoice.type === 'product_sale' && invoice.payment_status) {
      if (invoice.payment_status === 'paid') return { status: 'paid', label: 'Paid', color: 'green' };
      if (invoice.payment_status === 'partial') return { status: 'partial', label: 'Partial', color: 'orange' };
      return { status: 'unpaid', label: 'Unpaid', color: 'red' };
    }
    
    // For bookings or fallback calculation
    const totalAmount = invoice.invoice_total || invoice.total_amount || 0;
    const completedPayments = invoice.payments?.filter((p: any) => p.status === 'completed') || [];
    const totalPaid = completedPayments.reduce((sum: number, payment: any) => sum + payment.amount, 0);
    const balanceOwing = Math.max(0, totalAmount - totalPaid);
    
    if (balanceOwing === 0) return { status: 'paid', label: 'Paid', color: 'green' };
    if (totalPaid > 0) return { status: 'partial', label: 'Partial', color: 'orange' };
    return { status: 'unpaid', label: 'Unpaid', color: 'red' };
  };

  const handleViewInvoice = (invoice: any) => {
    // Only open modal for bookings, not product sales
    if (invoice.type === 'booking') {
      setSelectedInvoice(invoice);
      setIsPaymentsModalOpen(true);
    }
    // Product sales: do nothing (no modal)
  };

  const handleApplyPayment = (productSale: any) => {
    setSelectedProductSale(productSale);
    setIsApplyPaymentModalOpen(true);
  };

  const handleApplyBookingPayment = (booking: any) => {
    setSelectedBookingForPayment(booking);
    setIsRecordPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    // Refresh the invoices list
    window.location.reload();
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style jsx global>{`
        /* Hide scrollbar for invoice list */
        .invoice-scroll-container::-webkit-scrollbar {
          display: none;
        }
        
        .invoice-scroll-container {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      <div className="flex-1 flex flex-col bg-gray-100 overflow-y-auto">
        <div className="flex-1 flex flex-col w-full pt-2 px-0 pb-0">


        {/* Stats Summary */}
        <div className="grid grid-cols-3 gap-1.5 sm:gap-2 lg:gap-4 mb-1.5 sm:mb-2 lg:mb-3 px-2">
          <div className="bg-white rounded-md sm:rounded-lg lg:rounded-xl p-1.5 sm:p-2 lg:p-4 shadow-sm border border-gray-200">
            <div className="flex items-center space-x-1.5 sm:space-x-2 lg:space-x-3">
              <div className="p-1 sm:p-1.5 lg:p-2 bg-green-100 rounded-sm sm:rounded-md lg:rounded-lg">
                <Receipt className="h-2.5 w-2.5 sm:h-3 sm:w-3 lg:h-5 lg:w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600">Invoices</p>
                <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">{filteredInvoices.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-md sm:rounded-lg lg:rounded-xl p-1.5 sm:p-2 lg:p-4 shadow-sm border border-gray-200">
            <div className="flex items-center space-x-1.5 sm:space-x-2 lg:space-x-3">
              <div className="p-1 sm:p-1.5 lg:p-2 bg-green-100 rounded-sm sm:rounded-md lg:rounded-lg">
                <DollarSign className="h-2.5 w-2.5 sm:h-3 sm:w-3 lg:h-5 lg:w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600">Paid</p>
                <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
                  {filteredInvoices.filter(i => getPaymentStatus(i).status === 'paid').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-md sm:rounded-lg lg:rounded-xl p-1.5 sm:p-2 lg:p-4 shadow-sm border border-gray-200">
            <div className="flex items-center space-x-1.5 sm:space-x-2 lg:space-x-3">
              <div className="p-1 sm:p-1.5 lg:p-2 bg-red-100 rounded-sm sm:rounded-md lg:rounded-lg">
                <AlertCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 lg:h-5 lg:w-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600">Unpaid</p>
                <p className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
                  {filteredInvoices.filter(i => getPaymentStatus(i).status !== 'paid').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Invoices List */}
        <div className="flex flex-col bg-gray-100 rounded-none shadow-sm border-0 mb-0 mx-2">
          <div className="px-3 sm:px-4 lg:px-6 py-1.5 sm:py-2 lg:py-3 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">All Invoices</h2>
              <div className="relative">
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value as any)}
                  className="appearance-none bg-white border border-gray-300 rounded-lg px-2 py-1 sm:px-3 sm:py-1.5 pr-6 sm:pr-8 text-xs sm:text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 hover:border-gray-400 transition-colors"
                >
                  <option value="this-month">This Month</option>
                  <option value="last-month">Last Month</option>
                  <option value="this-year">This Year</option>
                  <option value="all-time">All Time</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          <div className="pt-2 px-2 pb-4 sm:p-3 lg:p-4 space-y-2 sm:space-y-3 lg:space-y-4">
            {filteredInvoices.length === 0 ? (
              <div className="flex items-center justify-center h-64 bg-white rounded-2xl border border-gray-200/50 shadow-lg">
                <div className="text-center">
                  <Receipt className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-gray-300" />
                  <p className="text-sm sm:text-base text-gray-500">No invoices found for selected date range</p>
                </div>
              </div>
            ) : (
              filteredInvoices.map((invoice, index) => {
                const paymentStatus = getPaymentStatus(invoice);
                const customer = invoice.customer;
                const totalAmount = invoice.invoice_total || invoice.total_amount || 0;
                const completedPayments = invoice.payments?.filter((p: any) => p.status === 'completed') || [];
                const totalPaid = completedPayments.reduce((sum: number, payment: any) => sum + payment.amount, 0);
                const balanceOwing = Math.max(0, totalAmount - totalPaid);

                return (
                  <div
                    key={invoice.id}
                    className="bg-white rounded-lg sm:rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 ease-out transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer flex"
                    onClick={() => handleViewInvoice(invoice)}
                    style={{
                      animationDelay: `${index * 50}ms`
                    }}
                  >
                    {/* Status Bar - Left Edge */}
                    <div className={`w-1 rounded-l-lg sm:rounded-l-xl ${
                      paymentStatus.color === 'green' ? 'bg-green-500' :
                      paymentStatus.color === 'orange' ? 'bg-orange-500' :
                      'bg-red-500'
                    }`} />
                    
                    <div className="p-3 sm:p-4 flex-1">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          <div className={`h-6 w-6 sm:h-8 sm:w-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                            invoice.type === 'product_sale' ? 'bg-blue-500' : 'bg-purple-500'
                          }`}>
                            {customer?.first_name?.[0]}{customer?.last_name?.[0]}
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900 truncate">
                              {customer ? `${customer.first_name} ${customer.last_name}` : 'Unknown Customer'}
                            </h3>
                            <p className="text-xs text-gray-500">#{invoice.invoice_number}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-1.5 py-0.5 sm:px-2 sm:py-1 text-xs font-medium rounded-full ${
                            invoice.type === 'product_sale' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {invoice.type === 'product_sale' ? 'Product Sale' : 'Booking'}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(invoice.invoice_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>

                      {/* Payment Status and Details */}
                      <div className="mb-2 sm:mb-3">
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-1 sm:mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            paymentStatus.color === 'green' ? 'bg-green-100 text-green-700' : 
                            paymentStatus.color === 'orange' ? 'bg-orange-100 text-orange-700' : 
                            'bg-red-100 text-red-700'
                          }`}>
                            {paymentStatus.label}
                          </span>
                          {balanceOwing > 0 && (
                            <span className="text-red-600 font-medium text-xs">
                              Owing: ${balanceOwing.toLocaleString()}
                            </span>
                          )}
                        </div>
                        
                        {/* Invoice Status Indicators */}
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          {/* Sent Status */}
                          <div className="flex items-center space-x-1">
                            {(() => {
                              const payments = invoice.payments || [];
                              const hasInvoiceSent = payments.some((p: any) => p.payment_token || p.stripe_checkout_session_id);
                              return (
                                <>
                                  <div className={`w-1.5 h-1.5 rounded-full ${hasInvoiceSent ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                  <span className="text-xs text-gray-600">
                                    {hasInvoiceSent ? 'Sent' : 'Not Sent'}
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                          
                          {/* Payment Link Status */}
                          <div className="flex items-center space-x-1">
                            {(() => {
                              const payments = invoice.payments || [];
                              const hasStripeLink = payments.some((p: any) => p.stripe_checkout_session_id);
                              return (
                                <>
                                  <div className={`w-1.5 h-1.5 rounded-full ${hasStripeLink ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                                  <span className="text-xs text-gray-600">
                                    {hasStripeLink ? 'Link' : 'No Link'}
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Amount and Action Button */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xl font-bold text-gray-900">
                            ${totalAmount.toLocaleString()}
                          </p>
                          {totalPaid > 0 && (
                            <p className="text-sm text-green-600">
                              Paid: ${totalPaid.toLocaleString()}
                            </p>
                          )}
                        </div>
                        <div className="text-right flex items-center space-x-2">
                          {/* Payment button for unpaid product sales */}
                          {invoice.type === 'product_sale' && paymentStatus.status !== 'paid' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApplyPayment(invoice);
                              }}
                              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            >
                              Apply Payment
                            </button>
                          )}

                          {/* Payment button for unpaid bookings */}
                          {invoice.type === 'booking' && paymentStatus.status !== 'paid' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApplyBookingPayment(invoice);
                              }}
                              className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            >
                              Apply Payment
                            </button>
                          )}
                          
                          {/* View invoice button for bookings */}
                          {invoice.type === 'booking' ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewInvoice(invoice);
                              }}
                              className="inline-flex items-center justify-center p-2 text-white bg-purple-600 hover:bg-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          ) : (
                            <div className="inline-flex items-center justify-center p-2 text-gray-400 bg-gray-200 rounded-lg cursor-not-allowed">
                              <FileText className="h-4 w-4" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* BetaPaymentsModal */}
      {selectedInvoice && selectedInvoice.type === 'booking' && (
        <BetaPaymentsModal
          isOpen={isPaymentsModalOpen}
          onClose={() => {
            setIsPaymentsModalOpen(false);
            setSelectedInvoice(null);
          }}
          bookingId={selectedInvoice.id}
          bookingNumber={selectedInvoice.booking_number}
          bookingData={{
            total_amount: selectedInvoice.total_amount,
            customer: selectedInvoice.customer,
            payments: selectedInvoice.payments
          }}
          booking={selectedInvoice}
          onUpdate={() => {
            // Refresh the invoices list
            window.location.reload();
          }}
        />
      )}

      {/* Apply Product Sale Payment Modal */}
      {selectedProductSale && (
        <ApplyProductSalePaymentModal
          isOpen={isApplyPaymentModalOpen}
          onClose={() => {
            setIsApplyPaymentModalOpen(false);
            setSelectedProductSale(null);
          }}
          productSale={selectedProductSale}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Record Booking Payment Modal */}
      {selectedBookingForPayment && (
        <RecordPaymentModal
          isOpen={isRecordPaymentModalOpen}
          onClose={() => {
            setIsRecordPaymentModalOpen(false);
            setSelectedBookingForPayment(null);
          }}
          onSuccess={handlePaymentSuccess}
          bookingId={selectedBookingForPayment.id}
          businessId={selectedBookingForPayment.business_id}
          bookingTotal={selectedBookingForPayment.total_amount || 0}
          amountPaid={selectedBookingForPayment.payments?.reduce((sum: number, payment: any) => 
            payment.status === 'completed' ? sum + payment.amount : sum, 0
          ) || 0}
        />
      )}
      </div>
    </>
  );
}

// Component for payroll content  
function PayrollContent() {
  return (
    <div className="h-full overflow-y-auto">
      <ManagePayrollPage />
    </div>
  );
}

// Component for invoice settings content
function InvoiceSettingsContent() {
  const { selectedBusiness } = useBusiness();
  
  const initialSettings = {
    // Company Information (will be populated from business data)
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyWebsite: '',
    
    // Invoice Styling
    primaryColor: '#3B82F6',
    headerBackground: '#F8FAFC',
    textColor: '#374151',
    accentColor: '#059669',
    
    // Invoice Terms
    paymentTerms: 'On receipt',
    lateFeesPolicy: '1.5% monthly service charge on overdue amounts',
    thankYouMessage: 'Thank you for your business!',
    
    // Invoice Settings
    includelogo: false,
    logoFile: null as File | null,
    logoUrl: null as string | null,
    showItemDescriptions: true,
    showTaxBreakdown: true,
    showVehicle: true,
    autoNumbering: true,
    invoicePrefix: 'INV-',
    
    // Stripe Settings
    request_tips_at_checkout: false,
  };
  
  const [invoiceSettings, setInvoiceSettings] = useState(initialSettings);
  const [savedSettings, setSavedSettings] = useState(initialSettings);
  const [activeTab, setActiveTab] = useState('preview');
  const [isSaving, setIsSaving] = useState(false);

  // Load invoice settings from database
  useEffect(() => {
    const loadInvoiceSettings = async () => {
      if (!selectedBusiness?.id) return;
      
      try {
        const settings = await InvoiceSettingsService.getInvoiceSettings(selectedBusiness.id);
        
        if (settings) {
          // Use existing settings from database
          const loadedSettings = {
            ...initialSettings,
            companyName: settings.company_name || selectedBusiness.name || 'Your Business Name',
            companyAddress: settings.company_address || selectedBusiness.address || '123 Business Street\nCity, State 12345',
            companyPhone: settings.company_phone || selectedBusiness.phone || '(555) 123-4567',
            companyEmail: settings.company_email || selectedBusiness.email || 'info@yourbusiness.com',
            companyWebsite: settings.company_website || `www.${selectedBusiness.name?.toLowerCase().replace(/\s+/g, '')}.com` || 'www.yourbusiness.com',
            primaryColor: settings.primary_color || '#3B82F6',
            headerBackground: settings.header_background || '#F8FAFC',
            textColor: settings.text_color || '#374151',
            accentColor: settings.accent_color || '#059669',
            includelogo: settings.include_logo || false,
            logoUrl: settings.logo_url || null,
            showItemDescriptions: settings.show_item_descriptions ?? true,
            showTaxBreakdown: settings.show_tax_breakdown ?? true,
            showVehicle: settings.show_vehicle ?? true,
            autoNumbering: settings.auto_numbering ?? true,
            invoicePrefix: settings.invoice_prefix || 'INV-',
            paymentTerms: settings.payment_terms || 'On receipt',
            lateFeesPolicy: settings.late_fees_policy || '1.5% monthly service charge on overdue amounts',
            thankYouMessage: settings.thank_you_message || 'Thank you for your business!',
            request_tips_at_checkout: settings.request_tips_at_checkout ?? false,
          };
          
          setInvoiceSettings(loadedSettings);
          setSavedSettings(loadedSettings);
        } else {
          // No settings found, use defaults with business info
          const defaultSettings = {
            ...initialSettings,
            companyName: selectedBusiness.name || 'Your Business Name',
            companyAddress: selectedBusiness.address || '123 Business Street\nCity, State 12345',
            companyPhone: selectedBusiness.phone || '(555) 123-4567',
            companyEmail: selectedBusiness.email || 'info@yourbusiness.com',
            companyWebsite: `www.${selectedBusiness.name?.toLowerCase().replace(/\s+/g, '')}.com` || 'www.yourbusiness.com',
          };
          
          setInvoiceSettings(defaultSettings);
          setSavedSettings(defaultSettings);
        }
      } catch (error) {
        console.error('Error loading invoice settings:', error);
        // Fall back to defaults with business info
        const fallbackSettings = {
          ...initialSettings,
          companyName: selectedBusiness.name || 'Your Business Name',
          companyAddress: selectedBusiness.address || '123 Business Street\nCity, State 12345',
          companyPhone: selectedBusiness.phone || '(555) 123-4567',
          companyEmail: selectedBusiness.email || 'info@yourbusiness.com',
          companyWebsite: `www.${selectedBusiness.name?.toLowerCase().replace(/\s+/g, '')}.com` || 'www.yourbusiness.com',
        };
        
        setInvoiceSettings(fallbackSettings);
        setSavedSettings(fallbackSettings);
      }
    };
    
    loadInvoiceSettings();
  }, [selectedBusiness]);

  const handleSettingChange = (key: string, value: any) => {
    setInvoiceSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Create object URL for preview
      const url = URL.createObjectURL(file);
      setInvoiceSettings(prev => ({
        ...prev,
        logoFile: file,
        logoUrl: url,
        includelogo: true // Auto-enable logo when uploaded
      }));
    }
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    // Compare all settings except File objects (compare by presence)
    const currentForComparison = {
      ...invoiceSettings,
      logoFile: invoiceSettings.logoFile ? 'file-present' : null,
    };
    const savedForComparison = {
      ...savedSettings,
      logoFile: savedSettings.logoFile ? 'file-present' : null,
    };
    
    return JSON.stringify(currentForComparison) !== JSON.stringify(savedForComparison);
  };

  const handleSaveSettings = async () => {
    if (!selectedBusiness?.id) return;
    
    setIsSaving(true);
    try {
      // Convert camelCase to snake_case for database
      const settingsToSave = {
        company_name: invoiceSettings.companyName,
        company_address: invoiceSettings.companyAddress,
        company_phone: invoiceSettings.companyPhone,
        company_email: invoiceSettings.companyEmail,
        company_website: invoiceSettings.companyWebsite,
        primary_color: invoiceSettings.primaryColor,
        header_background: invoiceSettings.headerBackground,
        text_color: invoiceSettings.textColor,
        accent_color: invoiceSettings.accentColor,
        include_logo: invoiceSettings.includelogo,
        logo_url: invoiceSettings.logoUrl || undefined,
        show_item_descriptions: invoiceSettings.showItemDescriptions,
        show_tax_breakdown: invoiceSettings.showTaxBreakdown,
        show_vehicle: invoiceSettings.showVehicle,
        auto_numbering: invoiceSettings.autoNumbering,
        invoice_prefix: invoiceSettings.invoicePrefix,
        payment_terms: invoiceSettings.paymentTerms,
        late_fees_policy: invoiceSettings.lateFeesPolicy,
        thank_you_message: invoiceSettings.thankYouMessage,
        request_tips_at_checkout: invoiceSettings.request_tips_at_checkout,
      };
      
      const success = await InvoiceSettingsService.saveInvoiceSettings(selectedBusiness.id, settingsToSave);
      
      if (success) {
        // Update saved settings to current settings after successful save
        setSavedSettings({ ...invoiceSettings });
        // TODO: Show success toast message
        console.log('Invoice settings saved successfully');
      } else {
        // TODO: Show error toast message
        console.error('Failed to save invoice settings');
      }
    } catch (error) {
      console.error('Error saving invoice settings:', error);
      // TODO: Show error toast message
    } finally {
      setIsSaving(false);
    }
  };

  const renderInvoicePreview = () => (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Invoice Header */}
      <div className="p-8" style={{ backgroundColor: invoiceSettings.headerBackground }}>
        <div className="flex justify-between items-start">
                     <div>
             {invoiceSettings.includelogo && (
               <div className="w-16 h-16 bg-gray-200 rounded-lg mb-4 flex items-center justify-center text-gray-400 text-xs overflow-hidden">
                 {invoiceSettings.logoUrl ? (
                   <img 
                     src={invoiceSettings.logoUrl} 
                     alt="Company Logo" 
                     className="w-full h-full object-contain"
                   />
                 ) : (
                   'LOGO'
                 )}
               </div>
             )}
            <h1 className="text-3xl font-bold" style={{ color: invoiceSettings.primaryColor }}>
              INVOICE
            </h1>
            <p className="text-sm text-gray-600 mt-1">#{invoiceSettings.invoicePrefix}2025-001</p>
          </div>
          <div className="text-right">
            <h2 className="text-xl font-semibold" style={{ color: invoiceSettings.textColor }}>
              {invoiceSettings.companyName}
            </h2>
            <div className="text-sm text-gray-600 mt-2 whitespace-pre-line">
              {invoiceSettings.companyAddress}
            </div>
            <div className="text-sm text-gray-600 mt-2">
              <div>{invoiceSettings.companyPhone}</div>
              <div>{invoiceSettings.companyEmail}</div>
              <div>{invoiceSettings.companyWebsite}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Details */}
      <div className="p-8">
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="font-semibold text-lg mb-2" style={{ color: invoiceSettings.textColor }}>Bill To:</h3>
            <div className="text-gray-600">
              <div className="font-medium">John Smith</div>
              <div>ABC Company</div>
              <div>456 Customer Ave</div>
              <div>City, State 54321</div>
              <div>(555) 987-6543</div>
            </div>
          </div>
          <div className="text-right">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">Invoice Date:</span>
                <span>{new Date().toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Due Date:</span>
                <span>{new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString()}</span>
              </div>
              {invoiceSettings.showVehicle && (
                <div className="flex justify-between">
                  <span className="font-medium">Vehicle:</span>
                  <span>2023 Toyota Camry</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Services Table */}
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-8">
          <div className="bg-gray-50 p-4">
            <div className="grid gap-2 font-semibold text-gray-700" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
              <div>Service</div>
              <div className="text-center">Quantity</div>
              <div className="text-right">Rate</div>
              <div className="text-right">Amount</div>
            </div>
          </div>
          <div className="divide-y">
            <div className="p-4">
              <div className="grid gap-2" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                <div>
                  <div className="font-medium">Full Detail Wash</div>
                  {invoiceSettings.showItemDescriptions && (
                    <div className="text-sm text-gray-600 mt-1">Complete exterior and interior detailing service</div>
                  )}
                </div>
                <div className="text-center">1</div>
                <div className="text-right">$150.00</div>
                <div className="text-right font-medium">$150.00</div>
              </div>
            </div>
            <div className="p-4">
              <div className="grid gap-2" style={{ gridTemplateColumns: '3fr 0.7fr 0.9fr 0.9fr' }}>
                <div>
                  <div className="font-medium">Paint Protection</div>
                  {invoiceSettings.showItemDescriptions && (
                    <div className="text-sm text-gray-600 mt-1">Ceramic coating application</div>
                  )}
                </div>
                <div className="text-center">1</div>
                <div className="text-right">$75.00</div>
                <div className="text-right font-medium">$75.00</div>
              </div>
            </div>
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-64">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>$225.00</span>
              </div>
              {invoiceSettings.showTaxBreakdown && (
                <div className="flex justify-between text-gray-600">
                  <span>Tax (8.5%):</span>
                  <span>$19.13</span>
                </div>
              )}
              <div className="border-t pt-2">
                <div className="flex justify-between font-bold text-lg" style={{ color: invoiceSettings.accentColor }}>
                  <span>Total:</span>
                  <span>$244.13</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold mb-2">Payment Terms:</h4>
              <p className="text-sm text-gray-600">{invoiceSettings.paymentTerms}</p>
              <p className="text-sm text-gray-600 mt-2">{invoiceSettings.lateFeesPolicy}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-medium" style={{ color: invoiceSettings.accentColor }}>
                {invoiceSettings.thankYouMessage}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSettingsPanel = () => (
    <div className="space-y-8">
      {/* Company Information */}
      <div className="bg-white rounded-lg p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold">Company Information</h3>
          <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
            Auto-populated from business settings
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          This information is automatically loaded from your business profile. You can customize it here specifically for invoices if needed.
        </p>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
            <input
              type="text"
              value={invoiceSettings.companyName}
              onChange={(e) => handleSettingChange('companyName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            <textarea
              value={invoiceSettings.companyAddress}
              onChange={(e) => handleSettingChange('companyAddress', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
              <input
                type="text"
                value={invoiceSettings.companyPhone}
                onChange={(e) => handleSettingChange('companyPhone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={invoiceSettings.companyEmail}
                onChange={(e) => handleSettingChange('companyEmail', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
            <input
              type="text"
              value={invoiceSettings.companyWebsite}
              onChange={(e) => handleSettingChange('companyWebsite', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
      </div>

             {/* Styling Options */}
       <div className="bg-white rounded-lg p-6">
         <h3 className="text-lg font-semibold mb-4">Invoice Styling</h3>
         
         {/* Logo Section */}
         <div className="mb-6">
           <div className="flex items-center mb-3">
             <input
               type="checkbox"
               checked={invoiceSettings.includelogo}
               onChange={(e) => handleSettingChange('includelogo', e.target.checked)}
               className="mr-3"
             />
             <label className="text-sm font-medium text-gray-700">Include Company Logo</label>
           </div>
           
           {invoiceSettings.includelogo && (
             <div className="ml-6 space-y-3">
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">Upload Logo</label>
                 <input
                   type="file"
                   accept="image/*"
                   onChange={handleLogoUpload}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                 />
                 <p className="text-xs text-gray-500 mt-1">Recommended: PNG or JPG, max 2MB</p>
               </div>
               
               {invoiceSettings.logoUrl && (
                 <div className="flex items-center space-x-3">
                   <div className="w-12 h-12 bg-gray-100 rounded border flex items-center justify-center overflow-hidden">
                     <img 
                       src={invoiceSettings.logoUrl} 
                       alt="Logo preview" 
                       className="w-full h-full object-contain"
                     />
                   </div>
                   <div className="flex-1">
                     <p className="text-sm text-gray-600">Logo uploaded successfully</p>
                     <button
                       type="button"
                       onClick={() => {
                         setInvoiceSettings(prev => ({
                           ...prev,
                           logoFile: null,
                           logoUrl: null
                         }));
                       }}
                       className="text-xs text-red-600 hover:text-red-700"
                     >
                       Remove logo
                     </button>
                   </div>
                 </div>
               )}
             </div>
           )}
         </div>

         {/* Color Settings */}
         <div className="grid grid-cols-2 gap-4">
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
             <input
               type="color"
               value={invoiceSettings.primaryColor}
               onChange={(e) => handleSettingChange('primaryColor', e.target.value)}
               className="w-full h-10 border border-gray-300 rounded-md"
             />
           </div>
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
             <input
               type="color"
               value={invoiceSettings.accentColor}
               onChange={(e) => handleSettingChange('accentColor', e.target.value)}
               className="w-full h-10 border border-gray-300 rounded-md"
             />
           </div>
         </div>
       </div>

             {/* Invoice Options */}
       <div className="bg-white rounded-lg p-6">
         <h3 className="text-lg font-semibold mb-4">Invoice Options</h3>
         <div className="space-y-4">
           <div className="flex items-center">
             <input
               type="checkbox"
               checked={invoiceSettings.showItemDescriptions}
               onChange={(e) => handleSettingChange('showItemDescriptions', e.target.checked)}
               className="mr-3"
             />
             <label className="text-sm font-medium text-gray-700">Show Service Descriptions</label>
           </div>
           <div className="flex items-center">
             <input
               type="checkbox"
               checked={invoiceSettings.showTaxBreakdown}
               onChange={(e) => handleSettingChange('showTaxBreakdown', e.target.checked)}
               className="mr-3"
             />
             <label className="text-sm font-medium text-gray-700">Show Tax Breakdown</label>
           </div>
           <div className="flex items-center">
             <input
               type="checkbox"
               checked={invoiceSettings.showVehicle}
               onChange={(e) => handleSettingChange('showVehicle', e.target.checked)}
               className="mr-3"
             />
             <label className="text-sm font-medium text-gray-700">Show Vehicle</label>
           </div>
         </div>
       </div>

            {/* Terms & Messages */}
      <div className="bg-white rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Terms & Messages</h3>
                <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Terms</label>
            <select
              value={invoiceSettings.paymentTerms}
              onChange={(e) => handleSettingChange('paymentTerms', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="On receipt">On receipt</option>
              <option value="Due within 30 days">Due within 30 days</option>
              <option value="Due within 90 days">Due within 90 days</option>
            </select>
          </div>
         <div>
           <label className="block text-sm font-medium text-gray-700 mb-2">Thank You Message</label>
           <input
             type="text"
             value={invoiceSettings.thankYouMessage}
             onChange={(e) => handleSettingChange('thankYouMessage', e.target.value)}
             className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
           />
         </div>
       </div>
     </div>

      {/* Stripe Settings - Only show when Stripe is connected */}
      {(selectedBusiness as any)?.stripe_connect_account_id && (
        <div className="bg-white rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Stripe Settings</h3>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-green-600 font-medium">Connected</span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Configure payment settings for your Stripe integration.
          </p>
          
          {/* Tip Settings */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                checked={invoiceSettings.request_tips_at_checkout}
                onChange={(e) => handleSettingChange('request_tips_at_checkout', e.target.checked)}
                className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700">
                  Request tips at checkout
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Allow customers to add tips when paying invoices through Stripe checkout. Tips will be tracked separately from the base payment amount.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col" data-invoice-settings-page>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">Invoice Settings</h1>
              {hasUnsavedChanges() && (
                <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">
                  Unsaved changes
                </span>
              )}
            </div>
            <p className="text-gray-600 mt-1">Configure your invoice template and branding</p>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={isSaving || !hasUnsavedChanges()}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-4 mt-4">
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'preview'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Preview
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeTab === 'settings'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Settings
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
        {activeTab === 'preview' ? (
          <div className="max-w-4xl mx-auto">
            {renderInvoicePreview()}
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            {renderSettingsPanel()}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TimeAccounting() {
  const { isPageDisabled } = useApplicationSettingsContext();
  const { canAccess } = usePermissionCheck();
  
  // Filter out disabled sub-pages
  const availablePages = timeAccountingPages.filter(page => {
    switch (page.id) {
      case 'timeclock':
        return !isPageDisabled('timeclock') && canAccess.timeclock;
      case 'payroll':
        return !isPageDisabled('payroll') && canAccess.payroll;
      case 'accounting-hub':
        return canAccess.accountingHub;
      case 'invoice-settings':
        return canAccess.invoiceSettings;
      default:
        return true;
    }
  });
  
  const [activePage, setActivePage] = useState(() => {
    // Default to first available page
    return availablePages.length > 0 ? availablePages[0].id : 'accounting-hub';
  });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // New state for mobile navigation pattern
  const [showMobileMenu, setShowMobileMenu] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
        // Reset to menu view when switching to mobile
        setShowMobileMenu(true);
      } else {
        // On desktop, always show sidebar
        setSidebarOpen(true);
        setShowMobileMenu(false);
      }
    };

    handleResize(); // Call immediately
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if current active page is still available, if not switch to first available
  useEffect(() => {
    if (!availablePages.some(page => page.id === activePage)) {
      const firstAvailable = availablePages.length > 0 ? availablePages[0].id : 'accounting-hub';
      setActivePage(firstAvailable);
    }
  }, [availablePages, activePage]);

  const currentPage = availablePages.find(page => page.id === activePage) || availablePages[0] || timeAccountingPages[0];

  // Handle mobile navigation
  const handleMobilePageSelect = (pageId: string) => {
    setActivePage(pageId);
    if (isMobile) {
      setShowMobileMenu(false);
    }
  };

  const handleMobileBackToMenu = () => {
    if (isMobile) {
      setShowMobileMenu(true);
    }
  };

  const renderPageContent = () => {
    // Only render content for pages that are available
    const isPageAvailable = availablePages.some(page => page.id === activePage);
    
    if (!isPageAvailable) {
      return <AccountingHubContent />;
    }
    
    switch (activePage) {
      case 'accounting-hub':
        return <AccountingHubContent />;
      case 'invoice-payments':
        return <InvoicePaymentsContent />;
      case 'timeclock':
        return <TimeclockContent />;
      case 'payroll':
        return <PayrollContent />;
      case 'invoice-settings':
        return <InvoiceSettingsContent />;
      default:
        return <AccountingHubContent />;
    }
  };

  return (
    <div className="flex bg-gray-100 relative overflow-hidden" data-time-accounting-page style={{ 
      height: 'calc(100vh - 3rem)',
      maxHeight: 'calc(100vh - 3rem)',
      minHeight: 0,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' 
    }}>
      {/* Mobile Menu View - Full Screen */}
      {isMobile && showMobileMenu && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-gray-800 z-50 flex flex-col pt-16">
          {/* Mobile Menu Header */}
          <div className="p-6 border-b border-gray-700 bg-gray-800">
            <div className="flex items-center space-x-3">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-lg">
                <Calculator className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Time / Accounting</h2>
                <p className="text-sm text-gray-300">Financial Management</p>
              </div>
            </div>
          </div>

          {/* Mobile Menu Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {availablePages.length > 0 ? (
              <nav className="space-y-3">
                {availablePages.map((page) => {
                  const Icon = page.icon;
                  
                  return (
                    <button
                      key={page.id}
                      onClick={() => handleMobilePageSelect(page.id)}
                      className={`w-full text-left p-4 rounded-xl transition-all duration-200 group ${
                        activePage === page.id 
                          ? 'bg-gray-700 border border-gray-600' 
                          : 'bg-gray-700/50 hover:bg-gray-700 border border-gray-600/50'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`p-3 rounded-lg transition-colors ${
                          activePage === page.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-600 text-gray-300 group-hover:bg-blue-600 group-hover:text-white'
                        }`}>
                          <Icon className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <div className={`text-base font-medium transition-colors ${
                            activePage === page.id
                              ? 'text-white'
                              : 'text-gray-200 group-hover:text-white'
                          }`}>
                            {page.label}
                          </div>
                          <div className={`text-sm mt-1 transition-colors ${
                            activePage === page.id
                              ? 'text-gray-300'
                              : 'text-gray-400 group-hover:text-gray-300'
                          }`}>
                            {page.description}
                          </div>
                        </div>
                        <ChevronRight className={`h-5 w-5 transition-colors ${
                          activePage === page.id
                            ? 'text-gray-300'
                            : 'text-gray-500 group-hover:text-gray-300'
                        }`} />
                      </div>
                    </button>
                  );
                })}
              </nav>
            ) : (
              <div className="p-4 text-center text-gray-400">
                <p className="text-sm">No pages available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop/Mobile Content View */}
      {(!isMobile || !showMobileMenu) && (
        <>
          {/* Left Sidebar - Desktop Only */}
          {!isMobile && (
            <div className={`${
              sidebarOpen ? 'w-80' : 'w-12'
            } flex-shrink-0 transition-all duration-300 ease-in-out bg-gray-800 border-r border-gray-700 flex flex-col overflow-hidden relative shadow-xl rounded-r-2xl`}>
              
              {/* Toggle Button */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="absolute top-4 right-4 p-2 bg-gray-700 rounded-xl hover:bg-gray-600 shadow-sm active:scale-95 z-20 transition-all duration-200"
                title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                {sidebarOpen ? (
                  <ChevronLeft size={18} className="text-gray-300" />
                ) : (
                  <ChevronRight size={18} className="text-gray-300" />
                )}
              </button>

              {/* Header */}
              {sidebarOpen && (
                <div className="p-6 border-b border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-lg">
                      <Calculator className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">Time / Accounting</h2>
                      <p className="text-sm text-gray-300">Financial Management</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Items */}
              {sidebarOpen && (
                <div className="flex-1 overflow-y-auto p-4">
                  {availablePages.length > 0 ? (
                    <nav className="space-y-2">
                      {availablePages.map((page) => {
                      const Icon = page.icon;
                      const isActive = activePage === page.id;
                      
                      return (
                        <button
                          key={page.id}
                          onClick={() => setActivePage(page.id)}
                          className={`w-full text-left p-3 rounded-xl transition-all duration-200 group ${
                            isActive
                              ? 'bg-gray-700 text-white shadow-sm'
                              : 'text-gray-300 hover:bg-gray-700 hover:text-white hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${
                              isActive
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 group-hover:bg-gray-600 group-hover:text-white'
                            }`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                              <div className={`text-sm font-medium ${
                                isActive ? 'text-white' : 'text-gray-300 group-hover:text-white'
                              }`}>
                                {page.label}
                              </div>
                              <div className={`text-xs ${
                                isActive ? 'text-gray-200' : 'text-gray-400 group-hover:text-gray-300'
                              }`}>
                                {page.description}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                    </nav>
                  ) : (
                    <div className="p-4 text-center text-gray-400">
                      <p className="text-sm">No pages available</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Mobile Content Header with Back Button */}
            {isMobile && !showMobileMenu && (
              <div className="bg-gray-800 border-b border-gray-700 p-4 flex items-center">
                <button
                  onClick={handleMobileBackToMenu}
                  className="p-2 rounded-lg hover:bg-gray-700 mr-3 transition-colors"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-300" />
                </button>
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-blue-600">
                    {React.createElement(currentPage.icon, {
                      className: "h-5 w-5 text-white"
                    })}
                  </div>
                  <div>
                    <h1 className="text-lg font-semibold text-white">{currentPage.label}</h1>
                    <p className="text-sm text-gray-300">{currentPage.description}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Page Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {renderPageContent()}
            </div>
          </div>
        </>
      )}
    </div>
  );
} 